const STORAGE_KEY = 'deepmox.pending-redeem';
const SESSION_KEY = 'deepmox.pending-redeem.session';
const AUTH_RESUME_KEY = 'deepmox.pending-redeem.auth-resume';
const MAX_AGE_MS = 30 * 60 * 1000;
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{7,63}$/;
const ATTRIBUTION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _.:/-]{0,79}$/;

export interface PromotionAttribution {
	sourceChannel?: string;
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
}

export interface PendingRedeemIntent {
	code: string;
	attribution: PromotionAttribution;
	recordedAt: number;
	sessionId: string;
	boundUserId: string | null;
}

export function recordPendingRedeemIntent(rawCode: string, attribution: PromotionAttribution = {}): boolean {
	const code = normalizeCode(rawCode);
	const storage = storageForCurrentTab();
	if (!code || !storage) return false;
	try {
		const sessionId = browserSessionId(storage);
		storage.setItem(STORAGE_KEY, JSON.stringify({ code, attribution: normalizeAttribution(attribution), recordedAt: Date.now(), sessionId, boundUserId: null } satisfies PendingRedeemIntent));
		return true;
	} catch {
		// Private-mode storage failures must not block a manual redeem flow.
		return false;
	}
}

export function readPendingRedeemIntent(currentUserId?: string): PendingRedeemIntent | null {
	const storage = storageForCurrentTab();
	if (!storage) return null;
	try {
		const raw = storage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const value = JSON.parse(raw) as Partial<PendingRedeemIntent>;
		const valid = typeof value.recordedAt === 'number'
			&& value.recordedAt <= Date.now() + 60_000
			&& Date.now() - value.recordedAt <= MAX_AGE_MS
			&& typeof value.sessionId === 'string'
			&& value.sessionId === browserSessionId(storage)
			&& typeof value.code === 'string'
			&& normalizeCode(value.code) === value.code
			&& (value.boundUserId === null || typeof value.boundUserId === 'string')
			&& isAttribution(value.attribution);
		if (!valid || (value.boundUserId && value.boundUserId !== currentUserId)) {
			if (!valid) clearPendingRedeemIntent();
			return null;
		}
		return value as PendingRedeemIntent;
	} catch {
		clearPendingRedeemIntent();
		return null;
	}
}

export function bindPendingRedeemIntentToUser(userId: string): void {
	const intent = readPendingRedeemIntent();
	const storage = storageForCurrentTab();
	if (!intent || !storage || !userId) return;
	try {
		storage.setItem(STORAGE_KEY, JSON.stringify({ ...intent, boundUserId: userId }));
	} catch {
		// The intent will naturally expire when storage is not writable.
	}
}

/** Marks that this tab opened authentication specifically to finish redeeming. */
export function markPendingRedeemAuthenticationResume(): void {
	const intent = readPendingRedeemIntent();
	const storage = storageForCurrentTab();
	if (!intent || !storage) return;
	try { storage.setItem(AUTH_RESUME_KEY, intent.sessionId); } catch { /* OAuth can still complete without automatic resume. */ }
}

/**
 * Consumes only the one-shot navigation marker. The redeem intent remains so
 * `/redeem` can restore and re-preview it after an OAuth full-page round trip.
 */
export function hasPendingRedeemAuthenticationResume(currentUserId: string): boolean {
	const intent = readPendingRedeemIntent(currentUserId);
	const storage = storageForCurrentTab();
	if (!intent || !storage) return false;
	try { return storage.getItem(AUTH_RESUME_KEY) === intent.sessionId; } catch { return false; }
}

export function consumePendingRedeemAuthenticationResume(currentUserId: string): boolean {
	const intent = readPendingRedeemIntent(currentUserId);
	const storage = storageForCurrentTab();
	if (!intent || !storage) return false;
	try {
		if (storage.getItem(AUTH_RESUME_KEY) !== intent.sessionId) return false;
		storage.removeItem(AUTH_RESUME_KEY);
		return true;
	} catch { return false; }
}

export function clearPendingRedeemIntent(): void {
	try {
		const storage = storageForCurrentTab();
		storage?.removeItem(STORAGE_KEY);
		storage?.removeItem(AUTH_RESUME_KEY);
	} catch {
		// Nothing else can safely be done when storage is unavailable.
	}
}

export function normalizePromotionAttribution(input: Record<string, string | null | undefined>): PromotionAttribution {
	return normalizeAttribution({
		sourceChannel: input.sourceChannel ?? undefined,
		utmSource: input.utmSource ?? undefined,
		utmMedium: input.utmMedium ?? undefined,
		utmCampaign: input.utmCampaign ?? undefined,
	});
}

function normalizeCode(rawCode: string): string | null {
	const code = rawCode.trim().toUpperCase();
	return CODE_PATTERN.test(code) ? code : null;
}

function normalizeAttribution(input: PromotionAttribution): PromotionAttribution {
	const values: Array<[keyof PromotionAttribution, string | undefined]> = [
		['sourceChannel', input.sourceChannel],
		['utmSource', input.utmSource],
		['utmMedium', input.utmMedium],
		['utmCampaign', input.utmCampaign],
	];
	return Object.fromEntries(values.flatMap(([key, value]) => {
		const normalized = value?.trim();
		return normalized && ATTRIBUTION_PATTERN.test(normalized) ? [[key, normalized]] : [];
	})) as PromotionAttribution;
}

function isAttribution(value: unknown): value is PromotionAttribution {
	if (!value || typeof value !== 'object') return false;
	const record = value as Record<string, unknown>;
	return ['sourceChannel', 'utmSource', 'utmMedium', 'utmCampaign'].every((key) => record[key] === undefined || (typeof record[key] === 'string' && ATTRIBUTION_PATTERN.test(record[key])));
}

function storageForCurrentTab(): Storage | null {
	try {
		return typeof window === 'undefined' ? null : window.sessionStorage;
	} catch {
		return null;
	}
}

function browserSessionId(storage: Storage): string {
	const existing = storage.getItem(SESSION_KEY);
	if (existing && /^[a-z0-9-]{16,128}$/i.test(existing)) return existing;
	const sessionId = crypto.randomUUID();
	storage.setItem(SESSION_KEY, sessionId);
	return sessionId;
}
